import { Component, EventEmitter, inject, Input, OnDestroy, Output } from '@angular/core';
import { LookupDataService } from '../../services/lookup-data.service';
import { AssessmentService } from '../../services/assessment.service';
import { MatDialog } from '@angular/material/dialog';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { LookupItem } from '../../models/lookup-item';
import { Person } from '../../data/person';
import { PersonDialogComponent } from '../person-dialog/person-dialog.component';
import { InitialAssessmentForm } from '../../data/initial-assessment-form';
import { IFormTypes } from '../../models/form-types';
import { ApplicantDetails } from '../../data/applicant-details';
import { BreakpointObserver } from '@angular/cdk/layout';
import { requiredListWhenYesValidator } from '../../validators/required-list-when-yes-validator';
import { IVisaType } from '../../data/visa-type';

@Component({
  selector: 'app-family-members',
  templateUrl: './family-members.component.html',
  styleUrl: './family-members.component.scss'
})
export class FamilyMembersComponent implements OnDestroy {
  
  @Input()model!: InitialAssessmentForm;
  @Input()formTypes!: IFormTypes;
  @Output() formReady = new EventEmitter<FormGroup>();
  @Input()applicantText: any;
  @Input()selectedForm: any;
  @Input()isVevo: any;
  @Input()isVevoEmployee: any;
  @Input()titles!: LookupItem[];
  @Input()relationTypes!: LookupItem[];
  @Input()maritalStatuses!: LookupItem[];
  @Input()educationLevels!: LookupItem[];
  @Input()countries!: LookupItem[];
  @Input()visaTypes!: IVisaType[];
  @Input()submitAttempted: boolean = false;
  
  displayedColumns: string[] = [];

    private destroy$ = new Subject<void>();
    private breakpointObserver = inject(BreakpointObserver);
    isMobile$ = this.breakpointObserver.observe(['(max-width: 768px)']).pipe(
      map(result => result.matches),
      takeUntil(this.destroy$)
    );
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


constructor(private dialog: MatDialog) { }

formBuilder = inject(NonNullableFormBuilder);

  otherApplicantsForm = this.formBuilder.group({
    otherApplicants: this.formBuilder.control('', Validators.required)
  },
  {
    validators: requiredListWhenYesValidator([
          {
            controlName: 'otherApplicants',
            errorKey: 'otherApplicantsRequired',
            getItems: () => this.model?.applicantDetails?.personList ?? []
          }])
  });

 ngOnInit() {
   


    this.initializeForm();
    this.updateDisplayedColumns();  
    this.formReady.emit(this.otherApplicantsForm);

  }

  initializeForm(){
        
    this.model = this.model || new InitialAssessmentForm();
    this.model.applicantDetails = this.model.applicantDetails || new ApplicantDetails();
        
    this.otherApplicantsForm?.patchValue({
      otherApplicants: this.model.applicantDetails.otherApplicants
    });  

    this.otherApplicantsForm.valueChanges.subscribe(value => {
      if (this.model.applicantDetails && value) {
          Object.assign(this.model.applicantDetails, value);
      }
    });

  }

  private updateDisplayedColumns() {
    this.displayedColumns = ['name', 'dob', 'passport'];
    
    if (!this.isVevo) {
      this.displayedColumns.push('marital');
    }
    
    this.displayedColumns.push('edit', 'delete');

  }


  add() {
     const dialogRef = this.dialog.open(PersonDialogComponent, {
          panelClass: 'dialog-panel-large',
          maxHeight: 'none',
          data: { person: null, 
          personType: 'otherFamilyMember',
          applicantName: this.model.applicantDetails?.firstName || 'the applicant',
          isVevo: this.isVevo,
          isVevoEmployee: this.isVevoEmployee, 
          titles: this.titles, 
          relationTypes: this.relationTypes, 
          maritalStatuses: this.maritalStatuses, 
          educationLevels: this.educationLevels, 
          countries: this.countries, 
          visaTypes: this.visaTypes 
        }
        });
        
        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.model.applicantDetails!.personList.push(result);
            this.model.applicantDetails!.personList = [...this.model.applicantDetails!.personList];
            this.otherApplicantsForm.controls.otherApplicants.updateValueAndValidity();
          }
        });
  }

remove(index: number): void {
    this.model.applicantDetails!.personList.splice(index, 1);
    this.model.applicantDetails!.personList = [...this.model.applicantDetails!.personList];
    this.otherApplicantsForm.controls.otherApplicants.updateValueAndValidity();
}

edit(index: number): void {
    const person = this.model.applicantDetails!.personList[index];
    const dialogRef = this.dialog.open(PersonDialogComponent, {
      panelClass: 'dialog-panel-large',
      maxHeight: 'none',
          data: { person: person, 
          personType: 'otherFamilyMember',
          applicantName: this.model.applicantDetails?.firstName || 'the applicant',
          isVevo: this.isVevo,
          isVevoEmployee: this.isVevoEmployee, 
          titles: this.titles, 
          relationTypes: this.relationTypes, 
          maritalStatuses: this.maritalStatuses, 
          educationLevels: this.educationLevels, 
          countries: this.countries, 
          visaTypes: this.visaTypes 
        }
    });
    
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.model.applicantDetails!.personList[index] = result;
        this.model.applicantDetails!.personList = [...this.model.applicantDetails!.personList];
        this.otherApplicantsForm.controls.otherApplicants.updateValueAndValidity();
      }
    });
  }



  get otherApplicantsQuestionText(): string {
  
    if(this.isVevo){
      return 'Would you like to include any additional person in this check? (eg Spouse/Children)?';
    }
     return 'Would you like to include any additional applicants in this application (e.g. Spouse/Children)?';
  
  } 

 get otherApplicantsTableText(): string {
    if(this.isVevo){
      return 'List all additional persons';
    }
    return 'List all additional applicants';
 }

  get safePersonList(): Person[] {
      return this.model.applicantDetails?.personList ?? [];
  }

  get AddButtonText(): string {
    if(this.isVevo){
      return 'Add a Person';
    }
    return 'Add Family Member';
  }

}
